import { Check } from "lucide-react";

/**
 * Tones of a timeline step, from the Pro app TimelineStep (jobbiz_app_pro_v2, components/ui):
 * done is a green checked dot, current an orange ring with a dot, upcoming an empty grey ring.
 */
const STEP_TONES = {
    done: {
        marker: "bg-green-500",
        line: "bg-green-200",
        title: "text-gray-900",
        description: "text-gray-600",
    },
    current: {
        marker: "border-2 border-yellow-500 bg-yellow-50",
        line: "bg-gray-200",
        title: "text-gray-900",
        description: "text-gray-600",
    },
    upcoming: {
        marker: "border-2 border-gray-200 bg-white",
        line: "bg-gray-200",
        title: "text-gray-400",
        description: "text-gray-400",
    },
};

/**
 * One step of a progress: its marker centred on the title and detail. The line to the next step
 * sits between the two markers, 4px away from each.
 */
const TimelineStep = ({ state, title, description, last = false }) => {
    const tone = STEP_TONES[state] ?? STEP_TONES.upcoming;

    return (
        <>
            <div className="flex items-center gap-3">
                <div className={`relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${tone.marker}`}>
                    {state === "current" ? (
                        <span className="timeline-current-wave absolute -inset-0.5 rounded-full bg-yellow-500" aria-hidden="true" />
                    ) : null}
                    {state === "done" ? <Check size={14} color="#ffffff" strokeWidth={2.5} aria-hidden="true" /> : null}
                    {state === "current" ? <div className="relative h-2 w-2 rounded-full bg-yellow-500" /> : null}
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                    <p className={`text-sm font-semibold leading-5 ${tone.title}`}>{title}</p>
                    {description ? <p className={`text-[13px] font-normal leading-[18px] ${tone.description}`}>{description}</p> : null}
                </div>
            </div>
            {/* 20px between two steps; the line also spans the space left above and below the
                centred markers (7px each side of a two-line text), minus its 4px gaps. */}
            {last ? null : (
                <div className="flex w-6 justify-center" aria-hidden="true">
                    <div className={`-my-[3px] h-[26px] w-0.5 rounded-full ${tone.line}`} />
                </div>
            )}
        </>
    );
};

/** "1er octobre 2026 à 9 h 42", in Paris time, as the Pro app writes it. */
const formatDateTimeLong = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZone: "Europe/Paris",
        })
            .formatToParts(date)
            .map((part) => [part.type, part.value])
    );
    const day = parts.day === "1" ? "1er" : parts.day;
    return `${day} ${parts.month} ${parts.year} à ${Number(parts.hour)} h ${parts.minute}`;
};

/** Applications still being processed: status "1" and not refused, as the Pro app counts them. */
export const pendingApplicationsCount = (mission) =>
    (Array.isArray(mission?.users) ? mission.users : []).filter(
        (entry) => String(entry?.status) === "1" && entry?.refused !== true
    ).length;

/**
 * Where a mission that still looks for someone stands: published, recruiting, then the temp
 * worker assigned. Same card as the Pro app (MissionRecruitment, Figma "Fiche mission ·
 * Recrutement"). The current step says what is being done, never that nothing happens: without
 * applications it reads "Nous recherchons un profil".
 */
const MissionRecruitment = ({ mission, labels }) => {
    const pending = pendingApplicationsCount(mission);
    const published = mission?.createdAt ? formatDateTimeLong(mission.createdAt) : null;

    return (
        // No rule around the card: its border already sets it apart from the header and the description.
        <div className="py-6">
            <div className="corner-smooth-12 border border-gray-100 bg-white p-4">
                <TimelineStep
                    state="done"
                    title={labels.published}
                    description={published ? labels.publishedOn.replace("{date}", published) : null}
                />
                <TimelineStep
                    state="current"
                    title={labels.recruiting}
                    description={
                        pending
                            ? (pending > 1 ? labels.pendingMany : labels.pendingOne).replace("{count}", pending)
                            : labels.searching
                    }
                />
                <TimelineStep state="upcoming" title={labels.assigned} description={labels.assignedHint} last />
            </div>
        </div>
    );
};

export default MissionRecruitment;
